<template>
  <div class="invoice-list">
    <div v-if="loading" class="text-center py-5">
      <div class="spinner-border text-primary" role="status">
        <span class="visually-hidden">Loading...</span>
      </div>
    </div>
    
    <div v-else-if="error" class="alert alert-danger">
      {{ error }}
    </div>
    
    <div v-else-if="invoices.length === 0" class="text-center py-5">
      <div class="alert alert-info">
        <i class="bi bi-info-circle me-2"></i>
        No invoices available.
        <span v-if="filter !== 'All'">Try changing your filter.</span>
      </div>
    </div>
    
    <div v-else-if="sortedInvoices.length === 0" class="text-center py-5">
      <div class="alert alert-warning">
        <i class="bi bi-search me-2"></i>
        No invoices match your search criteria.
        <button v-if="onClearFilters" class="btn btn-link p-0 ms-2" @click="onClearFilters">Clear filters</button>
      </div>
    </div>
    
    <div v-else class="card border-0 shadow-sm">
      <div class="card-body p-0">
        <div class="table-responsive">
          <table class="table table-hover mb-0">
            <thead class="table-light">
              <tr>
                <th @click="sortBy('invoiceNumber')" :class="{ sortable: !disableSorting }" class="invoice-header">
                  Invoice #
                  <i v-if="sortColumn === 'invoiceNumber'" :class="sortIcon"></i>
                </th>
                <th @click="sortBy('date')" :class="{ sortable: !disableSorting }" class="invoice-header">
                  Date
                  <i v-if="sortColumn === 'date'" :class="sortIcon"></i>
                </th>
                <th @click="sortBy('amount')" :class="{ sortable: !disableSorting }" class="invoice-header">
                  Amount
                  <i v-if="sortColumn === 'amount'" :class="sortIcon"></i>
                </th>
                <th @click="sortBy('status')" :class="{ sortable: !disableSorting }" class="invoice-header">
                  Status
                  <i v-if="sortColumn === 'status'" :class="sortIcon"></i>
                </th>
                <th class="invoice-header">Actions</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="invoice in paginatedInvoices" :key="invoice.id">
                <td>{{ invoice.invoiceNumber }}</td>
                <td>{{ formatDate(invoice.date) }}</td>
                <td>{{ formatCurrency(invoice.amount, invoice.calvi?.currencyCode || 'EUR') }}</td>
                <td>
                  <span 
                    class="badge"
                    :class="{
                      'bg-success': invoice.status === 'Paid',
                      'bg-warning': invoice.status === 'Pending',
                      'bg-danger': invoice.status === 'Overdue'
                    }"
                  >
                    {{ invoice.status }}
                  </span>
                </td>
                <td>
                  <router-link :to="`/invoices/${invoice.id}`" class="btn btn-sm btn-outline-secondary me-2">
                    View
                  </router-link>
                  <button 
                    class="btn btn-sm btn-outline-primary me-2"
                    @click="downloadInvoice(invoice.id)"
                  >
                    Download
                  </button>
                  <button 
                    v-if="invoice.status !== 'Paid'"
                    class="btn btn-sm btn-outline-success"
                    @click="payInvoice(invoice.id)"
                  >
                    Pay Now
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
      
      <!-- Pagination -->
      <div v-if="showPagination && totalPages > 1" class="card-footer bg-white d-flex justify-content-between align-items-center">
        <div>
          Showing {{ startItem }}-{{ endItem }} of {{ sortedInvoices.length }} invoices
        </div>
        <nav aria-label="Invoices pagination">
          <ul class="pagination mb-0">
            <li class="page-item" :class="{ disabled: currentPage === 1 }">
              <a class="page-link" href="#" @click.prevent="prevPage">Previous</a>
            </li>
            <li 
              v-for="page in totalPages" 
              :key="page" 
              class="page-item"
              :class="{ active: currentPage === page }"
            >
              <a class="page-link" href="#" @click.prevent="goToPage(page)">{{ page }}</a>
            </li>
            <li class="page-item" :class="{ disabled: currentPage === totalPages }">
              <a class="page-link" href="#" @click.prevent="nextPage">Next</a>
            </li>
          </ul>
        </nav>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, watch } from 'vue'
import { useStore } from 'vuex'

export default {
  name: 'InvoiceList',
  props: {
    invoices: {
      type: Array,
      required: true
    },
    loading: {
      type: Boolean,
      default: false
    },
    error: {
      type: [String, null],
      default: null
    },
    filter: {
      type: String,
      default: 'All'
    },
    search: {
      type: String,
      default: ''
    },
    limit: {
      type: Number,
      default: 0 // 0 means no limit
    },
    showPagination: {
      type: Boolean,
      default: true
    },
    disableSorting: {
      type: Boolean,
      default: false
    },
    defaultSortColumn: {
      type: String,
      default: 'date'
    },
    defaultSortDirection: {
      type: String,
      default: 'desc'
    },
    onClearFilters: {
      type: Function,
      default: null
    }
  },
  setup(props) {
    const store = useStore()
    
    // State
    const sortColumn = ref(props.defaultSortColumn || 'date')
    const sortDirection = ref(props.defaultSortDirection || 'desc')
    const currentPage = ref(1)
    const itemsPerPage = ref(10)
    
    // Make sure we apply the default sort values from props
    console.log('Default sort column:', props.defaultSortColumn)
    console.log('Default sort direction:', props.defaultSortDirection)
    
    // Reset page when props change
    watch(() => [props.filter, props.search, props.invoices.length], () => {
      currentPage.value = 1
    })
    
    // Computed properties
    const sortedInvoices = computed(() => {
      let result = [...props.invoices]
      
      // Filter by status
      if (props.filter !== 'All') {
        result = result.filter(invoice => invoice.status === props.filter)
      }
      
      // Filter by search query
      if (props.search) {
        const query = props.search.toLowerCase()
        result = result.filter(invoice => 
          invoice.invoiceNumber.toLowerCase().includes(query) || 
          formatDate(invoice.date).toLowerCase().includes(query) ||
          invoice.amount.toString().includes(query)
        )
      }
      
      // Sort
      result.sort((a, b) => {
        let valueA = a[sortColumn.value]
        let valueB = b[sortColumn.value]
        
        // Handle date formatting
        if (sortColumn.value === 'date') {
          valueA = new Date(valueA).getTime()
          valueB = new Date(valueB).getTime()
        }
        
        // Compare
        if (valueA < valueB) {
          return sortDirection.value === 'asc' ? -1 : 1
        }
        if (valueA > valueB) {
          return sortDirection.value === 'asc' ? 1 : -1
        }
        return 0
      })
      
      // Apply limit if specified
      if (props.limit > 0) {
        result = result.slice(0, props.limit)
      }
      
      return result
    })
    
    const totalPages = computed(() => {
      return Math.ceil(sortedInvoices.value.length / itemsPerPage.value)
    })
    
    const paginatedInvoices = computed(() => {
      if (!props.showPagination) {
        return sortedInvoices.value
      }
      
      const start = (currentPage.value - 1) * itemsPerPage.value
      const end = start + itemsPerPage.value
      return sortedInvoices.value.slice(start, end)
    })
    
    const startItem = computed(() => {
      if (sortedInvoices.value.length === 0) return 0
      return (currentPage.value - 1) * itemsPerPage.value + 1
    })
    
    const endItem = computed(() => {
      if (sortedInvoices.value.length === 0) return 0
      return Math.min(currentPage.value * itemsPerPage.value, sortedInvoices.value.length)
    })
    
    const sortIcon = computed(() => {
      return sortDirection.value === 'asc' ? 'bi bi-caret-up-fill' : 'bi bi-caret-down-fill'
    })
    
    // Methods
    const formatDate = (dateString) => {
      if (!dateString) return 'N/A'
      const date = new Date(dateString)
      return date.toLocaleDateString()
    }
    
    const formatCurrency = (amount, currency = 'EUR') => {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: currency
      }).format(amount)
    }
    
    const sortBy = (column) => {
      if (props.disableSorting) return
      
      if (sortColumn.value === column) {
        // Toggle direction if clicking the same column
        sortDirection.value = sortDirection.value === 'asc' ? 'desc' : 'asc'
      } else {
        // For dates, default to descending (newest first)
        // For other columns, default to ascending
        sortColumn.value = column
        sortDirection.value = column === 'date' ? 'desc' : 'asc'
      }
    }
    
    const prevPage = () => {
      if (currentPage.value > 1) {
        currentPage.value--
      }
    }
    
    const nextPage = () => {
      if (currentPage.value < totalPages.value) {
        currentPage.value++
      }
    }
    
    const goToPage = (page) => {
      currentPage.value = page
    }
    
    const downloadInvoice = (invoiceId) => {
      store.dispatch('invoices/downloadInvoice', invoiceId)
    }
    
    const payInvoice = async (invoiceId) => {
      try {
        await store.dispatch('invoices/markAsPaid', invoiceId)
      } catch (err) {
        console.error('Failed to process payment:', err)
      }
    }
    
    return {
      sortColumn,
      sortDirection,
      currentPage,
      sortedInvoices,
      paginatedInvoices,
      totalPages,
      startItem,
      endItem,
      sortIcon,
      formatDate,
      formatCurrency,
      sortBy,
      prevPage,
      nextPage,
      goToPage,
      downloadInvoice,
      payInvoice
    }
  }
}
</script>

<style scoped>
.sortable {
  cursor: pointer;
}

.sortable:hover {
  background-color: rgba(0, 0, 0, 0.05);
}

.invoice-header {
  position: relative;
  transition: all 0.2s ease;
}

/* Table styles overrides for better dark mode compatibility */
:deep(.table) {
  --bs-table-hover-color: inherit;
}

:deep([data-theme="dark"] .table-hover tbody tr:hover) {
  color: var(--text-light);
}

:deep([data-theme="dark"] .table thead.table-light), 
:deep([data-theme="dark"] thead.table-light) {
  background-color: #3D2053 !important;
  color: white !important;
}

:deep([data-theme="dark"] .table-light th) {
  background-color: #3D2053 !important;
  color: white !important;
}
</style>